import type {SocketData} from './signalr-socket'
import {MessageType, SignalRSocket} from './signalr-socket'
import {attr, controller, target} from '@github/catalyst'
import {updateContent} from '../updatable-content'

interface StepProgressUpdate {
  runId: number
  timelineId: string
  parentRecordId: string
  stepRecordId: string
  stepNumber: number
  changeId: number
  stepCompleted: boolean
}

@controller
class StreamingGraphJobElement extends HTMLElement {
  @target name: HTMLElement
  @target tooltip: HTMLElement
  @target ellipsis: HTMLElement

  @attr streamingUrl = ''
  @attr shouldStream = false
  #abortController = new AbortController()
  #streamingLogSocket = SignalRSocket.getInstance('WatchRunStepsProgressAsync')
  #seenJobs = new Set()

  #mutationObserver = new MutationObserver(() => {
    this.showTooltip()
  })

  connectedCallback() {
    this.#mutationObserver.observe(this, {attributes: true, childList: true, subtree: true})

    const xhrConnectionUrl = this.streamingUrl
    const {signal} = this.#abortController

    if (!this.shouldStream || !xhrConnectionUrl || this.allRunsConcluded()) {
      this.#abortController.abort()
      this.#streamingLogSocket.closeExistingSocket()
      return
    }

    this.#streamingLogSocket.startOrContinueStreaming(xhrConnectionUrl)
    this.#streamingLogSocket.addEventListener(
      'socketDidReceiveMessage',
      event => {
        if (!(event instanceof CustomEvent)) return

        this.processSocketMessage(event.detail)
      },
      {signal},
    )
  }

  disconnectedCallback() {
    this.#abortController.abort()
    this.#mutationObserver.disconnect()
  }

  processSocketMessage(data: SocketData<StepProgressUpdate[][]>) {
    if (data.type === MessageType.Ping) {
      // ping message, we can safely ignore
      return
    }

    if (data.target === 'stepProgressUpdates') {
      for (const args of data.arguments) {
        for (const stepUpdate of args) {
          this.#seenJobs.add(stepUpdate.parentRecordId)
        }
      }
    }

    for (const jobID of [...this.#seenJobs]) {
      const container = document.querySelector(`streaming-graph-job[data-external-id="${jobID}"]`) as HTMLElement

      if (container) {
        // if the job doesn't have a container on the graph, we'll ignore for now.
        // if the number of jobs changes, the action-graph parent element should
        // live update from WS and re-render the graph

        const isConcluded = container.getAttribute('data-concluded') === 'true'
        const hasURL = !!container.getAttribute('data-url')
        if (!isConcluded && hasURL) {
          // only update the container if it's not concluded, sometimes the normal
          // alive socket updates the job component before the socket msg comes
          updateContent(container)
        }
      }
    }
  }

  allRunsConcluded() {
    return document.querySelectorAll("streaming-graph-job[data-concluded='false']").length === 0
  }

  showTooltip() {
    if (!this.tooltip) return // if no tooltips return early, will only occur while FF isn't fully rolled out
    const isTruncated = this.name.scrollWidth > this.name.clientWidth
    const hasEllipsis = this.ellipsis != null

    if (isTruncated || hasEllipsis) {
      this.tooltip.style.display = ''
    } else {
      this.tooltip.style.display = 'none'
    }
  }
}
